/**
 * DEC-1146 (FEEDBACK-2): the app's only error boundary.
 *
 * WHAT IT FIXES. React 18 unmounts the ENTIRE tree when a render throws and no
 * boundary catches it. DECO has no boundary anywhere today, so any render
 * error — one undefined property in one component — turns the whole app into a
 * blank white page with no message, no recovery, and no way to tell anyone.
 *
 * WHY IT BELONGS TO THE FEEDBACK WORK. A blank page is exactly the moment a
 * user most needs to report something, and exactly the moment every reporting
 * surface has just been unmounted. This boundary keeps a reachable surface
 * alive at that moment. Once the Feedback widget lands (DEC-1148), its entry
 * point renders HERE — that is the point of the fallback, not the message.
 *
 * Deliberately minimal. The fallback must not itself depend on routing, auth,
 * org context, or any data fetch: whatever broke may have broken those too,
 * and a fallback that throws is worse than no fallback. Plain markup and a
 * reload button only.
 */

import React from 'react';
import { recordError } from '../services/errorBuffer';
// DEC-1148: reachable reporting surface for exactly this state.
import FeedbackWidget from './Feedback/FeedbackWidget';

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    // Feed the same buffer the global listeners write to, so a report filed
    // from the fallback carries the error that caused the fallback.
    // MESSAGE ONLY — componentStack is deliberately not captured (v1 security
    // ruling: no stack traces; a component stack leaks bundle structure).
    try {
      recordError(error?.message || 'render error', 'react');
    } catch {
      // Never let error handling throw inside error handling.
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          maxWidth: '520px',
          margin: '80px auto',
          padding: '0 20px',
          textAlign: 'center',
          fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
        }}
      >
        <div
          style={{
            fontSize: '32px', fontWeight: 200, fontStyle: 'italic',
            letterSpacing: '16px', color: '#1a365d', marginBottom: '32px',
          }}
        >
          DECO
        </div>

        <h1 style={{ fontSize: '20px', color: '#1a365d', marginBottom: '12px' }}>
          Something went wrong on this page
        </h1>
        <p style={{ color: '#4a5568', fontSize: '14px', lineHeight: 1.6, marginBottom: '28px' }}>
          This one’s on us, not you — nothing you did caused it and nothing you’ve
          saved is affected. Reloading usually clears it.
        </p>

        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            background: '#1a365d', color: '#fff', border: 0, borderRadius: '6px',
            padding: '14px 32px', fontSize: '15px', fontWeight: 600, cursor: 'pointer',
            minHeight: '44px',
          }}
        >
          Reload the page
        </button>

        {/* DEC-1148: the Feedback widget renders HERE — this is the moment it
            exists for. Everything else on the page has been unmounted, so
            without this the user has no way to tell us the app just blanked.
            It mounts itself as a fixed-position FAB, so it needs no layout
            from the fallback.

            No isMobile/userEmail passed: the defaults are correct for a crash
            surface, and reading auth state here would couple the fallback to a
            provider that may be what broke. The widget also reads its kill-
            switch flag defensively for the same reason — see its comment. */}
        <FeedbackWidget />

        <p style={{ color: '#718096', fontSize: '13px', marginTop: '28px' }}>
          If it keeps happening, you can also email{' '}
          <a href="mailto:deco@decoenergy.net?subject=DECO%20error" style={{ color: '#1a365d' }}>
            deco@decoenergy.net
          </a>
          .
        </p>
      </div>
    );
  }
}

export default AppErrorBoundary;
